// src/components/SortDropdown.jsx
//
// Slice P2 — Sort dropdown for the Portfolio control row.
//
// Why a new component instead of extending FilterDropdown:
//   FilterDropdown's option row is "label + count badge" (a numeric
//   chip on the right). The sort menu instead needs a two-line item
//   (title + subtitle) plus a checkmark on the active row, with a
//   wider min-width so the subtitles read clearly. Adding a renderItem
//   prop to FilterDropdown would entangle two visual idioms in one
//   component; building a sibling here keeps both clean. The a11y
//   model (combobox/listbox keyboard handling — ArrowDown/Up wrap,
//   Enter selects, Esc closes + returns focus, Tab falls through) is
//   mirrored exactly so users who learned it on Stage/Region/etc get
//   the same behavior here.
//
// Open/close state is owned by the parent — same pattern as
// FilterDropdown — so PortfolioView's existing "only one dropdown
// open at a time" + click-outside wiring covers this dropdown too.
//
// Props:
//   options       — Array<{ key, label, subtitle }> (from sortLocations.SORT_OPTIONS)
//   selectedKey   — currently active sort key
//   isOpen        — boolean (parent-controlled)
//   onToggle      — () => void
//   onSelect      — (key) => void

import React, { useEffect, useRef, useState } from "react";
import { T } from "../app/tokens.js";

export function SortDropdown({
  options = [],
  selectedKey,
  defaultKey,
  isOpen,
  onToggle,
  onSelect,
  // LSE-LIST-VIEW-SORT-UNIFICATION: when false, the dropdown is not the
  // active sort control (a column header is). Suppresses the checkmark and
  // blue highlight on all options, and the "filtered" treatment on the
  // trigger button so only one sort indicator is visible at a time.
  sortModeActive = true,
}) {
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const [focusedIdx, setFocusedIdx] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    const sel = options.findIndex((o) => o.key === selectedKey);
    const init = sel >= 0 ? sel : 0;
    setFocusedIdx(init);
    const id = requestAnimationFrame(() => {
      optionRefs.current[init]?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, options, selectedKey]);

  const onListKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = (focusedIdx + 1) % options.length;
      setFocusedIdx(next);
      optionRefs.current[next]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = (focusedIdx - 1 + options.length) % options.length;
      setFocusedIdx(next);
      optionRefs.current[next]?.focus();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = options[focusedIdx];
      if (opt) {
        onSelect(opt.key);
        triggerRef.current?.focus();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onToggle();
      triggerRef.current?.focus();
    }
  };

  // Do not fall back to options[0] — if the active key is an internal
  // default not listed in the menu (e.g. Locations' "smart" stage-priority
  // sort) selectedOption is intentionally undefined and the trigger shows
  // the defaultLabel / generic "Sort" text instead.
  const selectedOption = options.find((o) => o.key === selectedKey);
  // defaultKey (optional): the caller's "no sort applied" key. When supplied,
  // isFiltered is true only when the active key differs from the default.
  // Falls back to the legacy Locations behaviour (default key = "smart") when
  // defaultKey is not provided — "smart" is the stage-priority internal default
  // that is not listed in SORT_OPTIONS, so it evaluates to un-highlighted.
  const isFiltered = sortModeActive && (defaultKey
    ? selectedKey !== defaultKey
    : selectedKey && selectedKey !== "smart");

  // P7 — `data-dropdown-wrapper` / `data-dropdown-trigger` are read by
  // PortfolioView's document-level click-outside + Escape handlers; the
  // wrapper key matches the `openDropdown` value the parent uses ("sort").
  // Same pattern as FilterDropdown — see its header comment for the
  // ownership rationale.
  return (
    <div data-dropdown-wrapper="sort" style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        data-testid="sort-dropdown"
        data-dropdown-trigger="sort"
        aria-label="Sort by"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={onToggle}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "5px 11px 5px 11px", borderRadius: 7,
          border: `1.5px solid ${isFiltered ? T.blue : T.border}`,
          background: isFiltered ? T.blueSoft : T.white,
          fontSize: T.fsSm5, fontWeight: T.fwSemibold,
          color: isFiltered ? T.blue : T.dark,
          cursor: "pointer", whiteSpace: "nowrap",
          transition: "border-color 0.12s, background 0.12s",
        }}
      >
        <span data-testid="sort-dropdown-icon" aria-hidden="true" style={{ marginRight: 6, color: T.mid, fontSize: "1em" }}>
          ↑↓
        </span>
        {selectedOption?.chipLabel ?? selectedOption?.label ?? "Sort"}
        <span style={{
          fontSize: T.fsXxs, color: T.light, marginLeft: 2,
          display: "inline-block",
          transform: isOpen ? "rotate(180deg)" : "none",
          transition: "transform 0.15s",
        }}>
          ▾
        </span>
      </button>

      {isOpen && (
        <div
          role="listbox"
          data-testid="sort-dropdown-menu"
          onKeyDown={onListKeyDown}
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0,
            background: T.white, border: `1.5px solid ${T.border}`,
            borderRadius: 10, boxShadow: T.shadowDropdown,
            zIndex: 200, minWidth: 240, padding: "0 0 6px",
          }}
        >
          {/* P8 — menu header provides the "Sort by" anchor that the icon-only
              trigger can no longer carry visually. Keeps discoverability for
              sighted users while the trigger stays minimal. */}
          <div
            data-testid="sort-dropdown-header"
            style={{
              fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.mid,
              padding: "8px 12px",
              borderBottom: `1px solid ${T.border}`,
              letterSpacing: 0.3, textTransform: "uppercase",
            }}
          >
            Sort by
          </div>
          {options.map((opt, i) => {
            const sel = sortModeActive && opt.key === selectedKey;
            return (
              <div
                key={opt.key}
                ref={(el) => { optionRefs.current[i] = el; }}
                role="option"
                aria-selected={sel}
                tabIndex={-1}
                data-testid={`sort-dropdown-option-${opt.key}`}
                onClick={() => onSelect(opt.key)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 10px", borderRadius: 6,
                  background: sel ? T.blueSoft : "transparent",
                  cursor: "pointer", gap: 12,
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = T.lightGray; }}
                onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span style={{
                    fontSize: T.fsBase, fontWeight: sel ? 600 : 500,
                    color: sel ? T.blue : T.dark,
                    lineHeight: 1.2,
                  }}>
                    {opt.label}
                  </span>
                  <span style={{
                    fontSize: T.fsXs, fontWeight: T.fwRegular,
                    color: T.light, lineHeight: 1.3,
                  }}>
                    {opt.subtitle}
                  </span>
                </div>
                {sel && (
                  <span
                    aria-hidden="true"
                    style={{
                      fontSize: T.fsBase, fontWeight: T.fwBold, color: T.blue,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
